import { useState } from "react";
import { z } from "zod";
import { ProfileFieldSchema, type ProfileField } from "@starsach/shared-types";
import { useCreateProfile, usePublishProfile } from "../../api/hooks";
import { readFileText } from "../../lib/csv";
import { Card, ErrorState, btnPrimary, btnSecondary, inputClass } from "../../components/ui";

/** A profile travels as a small JSON document: its name and its fields, nothing environment-specific. */
export const ProfileExportSchema = z.object({ name: z.string().min(1).max(128), description: z.string().max(512).optional(), fields: z.array(ProfileFieldSchema) });
export type ProfileExport = z.infer<typeof ProfileExportSchema>;

export function exportProfile(doc: ProfileExport): void {
  const blob = new Blob([JSON.stringify(doc, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${doc.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.profile.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Creates the profile, then publishes its fields here - two calls, so the audit trail reads as it would by hand. */
export function ImportProfile({ environmentId, onDone }: { environmentId: string; onDone: () => void }) {
  const create = useCreateProfile(environmentId);
  const [doc, setDoc] = useState<ProfileExport>();
  const [problem, setProblem] = useState<string>();
  const [createdId, setCreatedId] = useState<string>();
  const publish = usePublishProfile(environmentId, createdId ?? "");

  const read = async (file: File | undefined) => {
    setProblem(undefined);
    setDoc(undefined);
    if (!file) return;
    try {
      const parsed = ProfileExportSchema.safeParse(JSON.parse(await readFileText(file)));
      if (!parsed.success) setProblem("That file is not a profile export: it needs a name and a list of fields.");
      else setDoc(parsed.data);
    } catch {
      setProblem("That file is not JSON.");
    }
  };

  return (
    <Card title="Import a profile">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!doc) return;
          create.mutate({ name: doc.name, ...(doc.description ? { description: doc.description } : {}) }, { onSuccess: (r) => setCreatedId(r.profileId) });
        }}
      >
        <label className="text-sm">
          <span className="block text-xs text-neutral-500">Profile file (.profile.json)</span>
          <input type="file" accept="application/json,.json" className={`${inputClass} w-80`} onChange={(e) => void read(e.target.files?.[0])} aria-describedby={problem ? "import-problem" : undefined} />
        </label>
        {doc && !createdId && (
          <span className="text-sm">
            <strong>{doc.name}</strong> · {doc.fields.length} field{doc.fields.length === 1 ? "" : "s"}
          </span>
        )}
        {!createdId ? (
          <button type="submit" className={btnPrimary} disabled={!doc || create.isPending}>
            {create.isPending ? "Creating…" : "Create from file"}
          </button>
        ) : (
          <button type="button" className={btnPrimary} disabled={publish.isPending || publish.isSuccess} onClick={() => doc && publish.mutate({ fields: doc.fields as ProfileField[] })}>
            {publish.isSuccess ? "Published" : publish.isPending ? "Publishing…" : `Publish ${doc?.fields.length ?? 0} fields here`}
          </button>
        )}
        <button type="button" className={btnSecondary} onClick={onDone}>
          {publish.isSuccess ? "Done" : "Cancel"}
        </button>
        {problem && <p id="import-problem" role="alert" className="basis-full text-xs text-red-700 dark:text-red-300">{problem}</p>}
        {create.isError && <ErrorState error={create.error} />}
        {publish.isError && <ErrorState error={publish.error} />}
        {publish.isSuccess && (
          <p role="status" className="basis-full text-sm text-green-800 dark:text-green-300">
            {publish.data.status === "published" ? `Published as v${publish.data.configVersion}.` : "Sent for a manager to approve."}
          </p>
        )}
      </form>
    </Card>
  );
}
